import React from "react";
import { useTranslation } from "react-i18next";
import {
  TEAM_EMBLEMS,
  getAvatarUrl,
  getContrastColor,
  getDisplayName,
  getInitials,
  getTeamColor,
  getTeamEmblemKey,
} from "./teamIdentity";

const EMBLEM_SIZES = {
  xs: { box: "h-7 w-7 rounded-lg", icon: 14 },
  sm: { box: "h-10 w-10 rounded-xl", icon: 18 },
  md: { box: "h-14 w-14 rounded-2xl", icon: 26 },
  lg: { box: "h-20 w-20 rounded-3xl", icon: 38 },
};

/** Colored emblem tile for a team. Decorative: the team name is always shown next to it. */
export const TeamEmblem = ({ team, size = "md", className = "" }) => {
  const color = getTeamColor(team);
  const Icon = TEAM_EMBLEMS[getTeamEmblemKey(team)];
  const { box, icon } = EMBLEM_SIZES[size] || EMBLEM_SIZES.md;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center shadow-lg ring-2 ring-white/40 ${box} ${className}`}
      style={{ backgroundColor: color, color: getContrastColor(color) }}
    >
      <Icon size={icon} strokeWidth={2.25} />
    </span>
  );
};

export const MemberAvatar = ({ user, size = "h-9 w-9 text-xs", className = "" }) => {
  const url = getAvatarUrl(user);
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#120239] font-bold text-white ring-2 ring-white ${size} ${className}`}
      title={getDisplayName(user)}
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span aria-hidden="true">{getInitials(user)}</span>
      )}
    </span>
  );
};

/** Overlapping avatars with a "+n" overflow. Names are exposed as one label. */
export const MemberStack = ({ members = [], max = 4, size }) => {
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="flex items-center" aria-label={members.map(getDisplayName).join(", ")} role="img">
      {shown.map((member, index) => (
        <MemberAvatar key={member.id} user={member} size={size} className={index ? "-ms-2" : ""} />
      ))}
      {extra > 0 && (
        <span className="-ms-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-700 ring-2 ring-white">
          +{extra}
        </span>
      )}
    </div>
  );
};

/** Circular win-rate meter; shows "-" when the team has no matches yet. */
export const WinRateRing = ({ value, size = 56, dark = false }) => {
  const { t } = useTranslation();
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = typeof value === "number" ? value : 0;
  return (
    <div
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={
        typeof value === "number"
          ? t("winRateLabel", { defaultValue: "Win rate {{value}}%", value })
          : t("noMatchesYet", { defaultValue: "No matches yet" })
      }
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth="6"
          className={dark ? "stroke-white/15" : "stroke-gray-200"}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          stroke="#FF9200"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - percent / 100)}
          className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-700"
        />
      </svg>
      <span
        aria-hidden="true"
        className={`absolute text-xs font-bold tabular-nums ${dark ? "text-white" : "text-gray-900"}`}
      >
        {typeof value === "number" ? `${value}%` : "-"}
      </span>
    </div>
  );
};

const TYPE_STYLES = {
  PvP: "bg-rose-100 text-rose-700",
  Coop: "bg-emerald-100 text-emerald-700",
  PvE: "bg-sky-100 text-sky-700",
};

export const SessionTypeChip = ({ type, count }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
      TYPE_STYLES[type] || "bg-gray-100 text-gray-700"
    }`}
  >
    {type}
    {typeof count === "number" && <span className="tabular-nums opacity-80">×{count}</span>}
  </span>
);

export const StatTile = ({ icon: Icon, label, value, accent = "text-orange-500", dark = false }) => (
  <div
    className={`rounded-2xl p-4 ${
      dark ? "border border-white/10 bg-white/5" : "bg-white shadow-sm ring-1 ring-gray-100"
    }`}
  >
    <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-gray-500"}`}>
      <Icon size={16} className={accent} aria-hidden="true" />
      {label}
    </div>
    <p className={`mt-2 text-2xl font-bold tabular-nums ${dark ? "text-white" : "text-gray-900"}`}>{value}</p>
  </div>
);

export const SkeletonBlock = ({ className = "" }) => (
  <div className={`motion-safe:animate-pulse rounded-3xl bg-gray-200 ${className}`} />
);
